import { cloneDeep } from 'lodash-es';
import { empty } from '@/utils/index.js';
import authUserApi from '@/api/authUser.js';
import axios, { getOnce } from '@/plugins/axios.js';
import { wait } from '@/plugins/vue-wait.ts';
import { useTimedPageAction } from '@/hooks/useTimedPageAction.ts';
import { useAccountStore } from '@/store/modules/account/store.js';
import { useDashboardStore } from '@/store/modules/dashboard/store.js';
import Moment from '@/value-objects/Moment.js';
import TimedPageActions from '@/constants/TimedPageActions.ts';
import { assertEntityImport } from '@/utils/assertEntityImport.ts';
import { measureStartupPhase } from '@/telemetry/startupTiming.ts';

export default {
    async setParentAuthUser(user) {
        const { default: AuthUser } = await import('@/entities/AuthUser.js');
        assertEntityImport(AuthUser, 'AuthUser');
        this.parentAuthUser = new AuthUser(user);
    },

    async setContextAccountUsers(users, accountId = null, request = null) {
        const { default: User } = await import('@/entities/User.js');
        assertEntityImport(User, 'User');

        const accountStore = useAccountStore();
        if (
            (request && !accountStore.isCurrentContextAccountRequest(request)) ||
            (accountId && Number(accountStore.contextAccount?.id) !== Number(accountId))
        ) {
            return;
        }

        this.users = Object.fromEntries(users.map((user) => [user.id, new User(user)]));
        this.contextAccountUsersAccountId = accountId ?? accountStore.contextAccount?.id ?? null;
    },

    getUserById(id, withParentId = true) {
        if (!id) {
            return null;
        }

        if (!withParentId) {
            return this.users[id] || null;
        }

        return (
            this.users[id] ||
            Object.values(this.users).find((user) => user.parent_user_id == id) ||
            null
        );
    },

    getUserNameById(id, withParentId = true) {
        return this.getUserById(id, withParentId)?.fullName || '';
    },

    async fetchAuthUser(bypassCache = false) {
        const isActualLogin = !this.parentAuthUser?.id;

        // Only create the hook and track if it's an actual login
        const timedAction = isActualLogin
            ? useTimedPageAction({ action: TimedPageActions.USER_LOGIN, additionalData: {} })
            : null;

        wait.start('fetching.authUser');

        try {
            const fetchFn = async () => {
                const [profile, abilities, featurePreviews] = await measureStartupPhase(
                    'auth_profile_abilities_previews',
                    () =>
                        Promise.all([
                            authUserApi.getProfile(
                                bypassCache ? { bypassCache: Moment.now().timestamp() } : {}
                            ),
                            authUserApi.getAbilities(
                                bypassCache ? { bypassCache: Moment.now().timestamp() } : {}
                            ),
                            authUserApi.getFeaturePreviews(
                                bypassCache ? { bypassCache: Moment.now().timestamp() } : {}
                            ),
                        ])
                );

                await this.setParentAuthUser(profile);
                this.authUserAbilities = abilities;
                this.authUserFeaturePreviews = featurePreviews;

                await measureStartupPhase('account_list', () =>
                    useAccountStore().fetchAccounts(bypassCache)
                );
            };

            // If it's an actual login, wrap with timing; otherwise just run it
            if (timedAction) {
                await timedAction.run(fetchFn);
            } else {
                await fetchFn();
            }
        } catch (error) {
            console.error('Error fetching authenticated user:', error);
            throw error;
        } finally {
            wait.end('fetching.authUser');
        }
    },

    async clearAuthUser() {
        const { default: AuthUser } = await import('@/entities/AuthUser.js');
        assertEntityImport(AuthUser, 'AuthUser');
        this.parentAuthUser = new AuthUser();
        this.authUserAbilities = [];
        this.authUserFeaturePreviews = [];
    },

    async fetchUsersByAccountId(accountId, bypassCache = false, request = null) {
        wait.start('fetching.contextUsers');
        try {
            const response = await getOnce(
                `v1/context-account/${accountId}/users`,
                bypassCache ? { bypassCache: Moment.now().timestamp() } : {}
            );

            if (
                !response?.data ||
                (request && !useAccountStore().isCurrentContextAccountRequest(request))
            ) {
                return null;
            }

            if (request) {
                return response.data;
            }

            await this.setContextAccountUsers(response.data, accountId);

            return response.data;
        } catch (error) {
            console.error('Error fetching users:', error);
        } finally {
            wait.end('fetching.contextUsers');
        }

        return null;
    },

    updateParentAuthUser(newData, syncChilds = false) {
        Object.assign(this.parentAuthUser, newData);

        if (syncChilds) {
            this.parentAuthUser.children.forEach((child) => Object.assign(child, newData));
        }
    },

    updateSession(type, value, overwrite = true) {
        const session = cloneDeep(this.session);
        const dashboardType = useDashboardStore().dashboardType;
        const dashboardDependantOptions = [
            'dashboard_column',
            'dashboard_column_order',
            'dashboard_dates',
            'dashboard_expand',
            'dashboard_filter',
            'order',
        ];

        if (dashboardDependantOptions.includes(type)) {
            if (empty(session[type])) {
                session[type] = {};
            }

            if (!session[type][dashboardType]) {
                session[type][dashboardType] = {};
            }

            if (overwrite) {
                session[type][dashboardType] = value;
            } else {
                session[type][dashboardType] = {
                    ...session[type][dashboardType],
                    ...value,
                };
            }
        } else if (overwrite) {
            session[type] = value;
        } else {
            session[type] = { ...session[type], ...value };
        }

        this.setAuthUserSession(session);

        if (session.id) {
            axios.put(`v1/sessions/${session.id}`, session);
        }
    },

    setAuthUserSession(session) {
        this.parentAuthUser.session = session;
    },
};
